// EditPostDialog.tsx — text-only post edit. Media renders as a locked preview
// with a muted note; only `body` is ever sent to the server (see
// postsService.editPost / PostViewSet.partial_update, which ignores/rejects media).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Dialog, Button, Textarea } from '@/ui';
import type { Post } from '@/core/models/post';
import { PostMedia } from './PostMedia';
import { postImages } from './postImages';

export interface EditPostDialogProps {
  post: Post;
  onClose: () => void;
  onSave: (body: string) => void | Promise<void>;
  /** 1.23.00 Phase 4: a client-side GUESS that a free account's 30-minute edit
   * window has passed. Renders an advisory note above the editor and nothing
   * else — Save stays enabled and the attempt still goes to the server, which
   * is the only authority (useOwnPostActions.saveEdit surfaces its
   * `edit_window_expired` rejection). This used to hard-hide Save, which
   * locked paying members out whenever the `premium` mirror, the webhook or
   * the browser clock disagreed with the server. */
  editWindowAdvisory?: boolean;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LockedMediaWrap = styled.div`
  pointer-events: none;
  opacity: 0.7;
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
`;

const LockedNote = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const WindowNote = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.5;
  color: ${({ theme }) => theme.color.textMuted};
`;

const UpgradeLink = styled(Link)`
  color: ${({ theme }) => theme.color.accentCyan};
  font-weight: 600;
`;

export function EditPostDialog({ post, onClose, onSave, editWindowAdvisory }: EditPostDialogProps) {
  const [draft, setDraft] = useState(post.body);
  const [saving, setSaving] = useState(false);
  const hasMedia = postImages(post).length > 0 || (post.media && post.media !== '');

  async function handleSave() {
    const trimmed = draft.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      await onSave(trimmed);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} title="Edit post">
      <Body>
        {/* Advisory only — never a gate. Save stays enabled below and the
            server decides; see EditPostDialogProps.editWindowAdvisory. */}
        {editWindowAdvisory && (
          <WindowNote>
            Free accounts can edit a post for 30 minutes after it&apos;s posted, and that
            window looks like it has passed for this one. Saving may be turned down.{' '}
            <UpgradeLink to="/premium">Pro removes the limit</UpgradeLink>.
          </WindowNote>
        )}
        <Textarea
          label="Post body"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
        />
        {hasMedia && (
          <>
            <LockedMediaWrap aria-hidden="true">
              <PostMedia post={post} media={post.media} />
            </LockedMediaWrap>
            <LockedNote>Photos and media can&apos;t be changed.</LockedNote>
          </>
        )}
        <Footer>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={draft.trim().length === 0 || saving}
            onClick={() => void handleSave()}
          >
            Save
          </Button>
        </Footer>
      </Body>
    </Dialog>
  );
}
